import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { animate, AnimationBuilder, AnimationPlayer, style } from '@angular/animations';
import { MatRippleModule } from '@angular/material/core';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';

let nextUniqueId = 0;

export interface RxStarCheckboxChange {
	source: RxStarCheckboxComponent;
	checked: boolean;
	event: Event;
}

const uncheckedStyle = style({
	stroke: '#999999',
	fill: 'transparent'
});
const checkedStyle = style({
	stroke: '#0091c9',
	fill: '#00ABED'
});

const checkedUnchecked = [checkedStyle, animate('300ms ease-in-out', uncheckedStyle)];
const uncheckedChecked = [uncheckedStyle, animate('300ms ease-in-out', checkedStyle)];
const initialChecked = [checkedStyle, animate('300ms ease-in-out', checkedStyle)];
const initialUnchecked = [uncheckedStyle, animate('300ms ease-in-out', uncheckedStyle)];

@Component({
	selector: 'rx-star-checkbox',
	templateUrl: './rx-star-checkbox.component.html',
	styleUrls: ['./rx-star-checkbox.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'rx-star-checkbox',
		'[class.rx-star-checkbox-checked]': 'checked',
		'[class.rx-star-checkbox-disabled]': 'disabled'
	},
	standalone: true,
	imports: [NgClass, ExtendedModule, MatRippleModule]
})
export class RxStarCheckboxComponent implements AfterViewInit {
	private uniqueId = `rx-star-checkbox-${++nextUniqueId}`;

	/** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
	@Input() id: string = this.uniqueId;
	@Input()
	set checked(value: boolean) {
		if (value !== this.innerChecked) {
			this.runBlueAnimation(value, this.innerChecked);
			this.innerChecked = value;
		}
	}
	get checked(): boolean {
		return this.innerChecked;
	}
	@Input() disabled: boolean;

	@Output() readonly change = new EventEmitter<RxStarCheckboxChange>();
	@ViewChild('input') inputElement: ElementRef<HTMLInputElement>;
	@ViewChild('svg') svg: ElementRef<SVGElement>;

	released = true;

	private innerChecked: boolean;
	private player: AnimationPlayer;

	/** Returns the unique id for the visual hidden input. */
	get inputId(): string {
		return `${this.id || this.uniqueId}-input`;
	}
	onReleased(released: boolean) {
		if (!this.disabled) {
			this.released = released;
		}
	}

	constructor(private animationBuilder: AnimationBuilder) {}

	ngAfterViewInit(): void {
		this.runBlueAnimation(this.innerChecked, undefined);
	}

	onInteractionEvent(event: Event) {
		event.stopPropagation();
	}

	onInputClick(event: Event) {
		// We have to stop propagation for click events on the visual hidden input element.
		// By default, when a user clicks on a label element, a generated click event will be
		// dispatched on the associated input element. Since we are using a label element as our
		// root container, the click event on the `checkbox` will be executed twice.
		// The real click event will bubble up, and the generated click event also tries to bubble up.
		// This will lead to multiple click events.
		// Preventing bubbling for the second event will solve that issue.
		event.stopPropagation();
		if (this.disabled) {
			return;
		}
		this.toggle();
		this.emitChangeEvent(event);
	}

	toggle(): void {
		this.checked = !this.checked;
	}

	private emitChangeEvent(event: Event) {
		const changeEvent: RxStarCheckboxChange = {
			source: this,
			checked: this.checked,
			event
		};

		this.change.emit(changeEvent);

		// Assigning the value again here is redundant, but we have to do it in case it was
		// changed inside the `change` listener which will cause the input to be out of sync.
		if (this.inputElement) {
			this.inputElement.nativeElement.checked = this.checked;
		}
	}

	private runBlueAnimation(newValue: boolean, prevValue: boolean | undefined) {
		this.player?.destroy();

		const factory = this.animationBuilder.build(this.getState(newValue, prevValue));

		if (this.svg?.nativeElement) {
			this.player = factory.create(this.svg.nativeElement);
			this.player?.play();
		}
	}

	private getState(newValue: boolean, prevValue: boolean | undefined) {
		if (prevValue === undefined) {
			return newValue ? initialChecked : initialUnchecked;
		}

		return newValue ? uncheckedChecked : checkedUnchecked;
	}
}
